import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { useDispatch, useSelector } from 'react-redux'
import { clearCountryStates, clearStates } from 'store/countryStates/slice'
import { getStates } from 'store/countryStates/thunks'
import { selectCourses } from 'store/courses/slice'
import { searchCoursesByCourseOrClubName } from 'store/courses/thunks'
import styled from 'styled-components'
import CourseTable from './CourseTable'
import SearchBar from './SearchBar'
import { Pagination } from '@mui/material'

export default function Search(): JSX.Element {
  const [countriesWithStates, setCountriesWithStates] = useState<Country[]>([])
  const [currentPage, setCurrentPage] = useState<number>(1)
  const { courses, error, status, totalCount } = useSelector(selectCourses)
  const intl = useIntl()
  const form = useForm<CourseEditorSearchForm>()
  const { handleSubmit, setValue, getValues } = form
  const dispatch = useDispatch()

  const handleCountryChange = (countries: Country[]) => {
    if (countries.length === 0 || countries[0].hasStates === false) {
      setValue('state', null)
      setCountriesWithStates([])
      dispatch(clearStates())
      setCurrentPage(1)
    }

    const removedCountries = countriesWithStates.filter((country: Country) => !countries.includes(country))
    removedCountries.map((country: Country) => {
      setCountriesWithStates(countriesWithStates.filter((c: Country) => c !== country))
      dispatch(clearCountryStates(country))
      return country
    })

    countries.map((country: Country) => {
      if (country.hasStates && !countriesWithStates.includes(country)) {
        setCountriesWithStates([...countriesWithStates, country])
        dispatch(getStates(country.id))
      }
      return country
    })
  }

  const searchCourses = (data: CourseEditorSearchForm, page: number): void => {
    const payload = {
      courseOrClubName: encodeURIComponent(data.courseOrClubName),
      countryId: data.country?.key && data.country.key !== 'all' ? Number(data.country.key) : undefined,
      stateId: data.state?.key ? Number(data.state.key) : undefined,
      page: page,
      limit: 10,
      offset: 10 * (page - 1),
    }
    dispatch(searchCoursesByCourseOrClubName(payload))
  }

  const onSubmit = (data: CourseEditorSearchForm) => {
    searchCourses(data, 1)
    setCurrentPage(1)
  }

  const handlePageChange = (page: number) => {
    searchCourses(getValues(), page)
    setCurrentPage(page)
  }

  return (
    <Container>
      <Title>{intl.formatMessage({ id: 'courseEditor.searchTitle' })}</Title>
      <SearchBar
        countriesWithStates={countriesWithStates}
        form={form}
        handleCountryChange={handleCountryChange}
        onSubmit={handleSubmit(onSubmit)}
      />
      <CourseTable courses={courses} error={error} status={status} />
      {courses.length > 0 && (
        <PaginationContainer>
          <Pagination
            count={Math.ceil(totalCount / 10)}
            page={currentPage}
            onChange={(_event, page) => handlePageChange(page)}
            shape="rounded"
          />
        </PaginationContainer>
      )}
    </Container>
  )
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 100px;
  margin-left: 50px;
  align-items: flex-start;
  justify-content: flex-start;
  width: 100%;
  max-width: 995px;
`

const Title = styled.h1`
  font-size: 36px;
  font-weight: 300;
  margin-bottom: 10px;
  text-align: left;
  text-transform: uppercase;
`

const PaginationContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`
